const logDefaults = {
    env: 'dev',
    trace: false,
    newLine: false,
    timer: false,
    logLevel: {
        trace: 'dev',
        method: 'dev',
        log: 'dev',
    }
};

export function ClogFactory({ ...config } = {}) {
    const bindings = {}
    const timeIDs = {}
    const logTypes = {
        warning: 'warning',
        error: 'error',
        log: 'log',
    }
    const logPrefix = {
        warning: '%cWarning: ',
        error: '%cError: ',
    }
    const logLevels = {
        dev: 1,
        develop: 1,
        demo: 2,
        prod: 3,
        production: 3,
    }

    let fetchedConfig = false;
    if (config.getConfig && !fetchedConfig) {
        fetchedConfig = true;
        fetch(config.getConfig)
            .then(res => res.json())
            .then(res => updateConfig(res));
    }

    let { method, trace, newLine, timer } = { ...logDefaults, ...config };
    let logLevel = Object.keys(logDefaults.logLevel).reduce((acc, key) => {
        acc[key] = logLevels[config.logLevel[key] || logDefaults.logLevel[key]] >= logLevels[config.env];
        return acc;
    }, {});

    const updateConfig = (newConfig) => {
        config = { ...config, ...newConfig };
        method = config.method;
        trace = config.trace;
        newLine = config.newLine;
        timer = config.timer;

        logLevel = Object.keys(logDefaults.logLevel).reduce((acc, key) => {
            acc[key] = logLevels[config.logLevel[key] || logDefaults.logLevel[key]] >= logLevels[config.env];
            return acc;
        }, {});
    }

    const uniqueId = (name) => {
        if (!timeIDs[name]) timeIDs[name] = 1;
        return `${name} (${timeIDs[name]++})`;
    }

    function logFunc(...args) {
        if (!logLevel.log) return;

        if (args[0] instanceof Array) {
            const logType = args[1] ?? console.log
            logType(...[...args[0]]);
            if (method && logLevel.method) {
                logType(`\t> ${method}`);
                method = null;
            }
        } else {
            console.log(...args);
        }

        if (trace && logLevel.trace) console.trace();
        if (newLine) console.log('');
    };

    logFunc.log = (...arg) => logFunc(arg);

    logFunc.warn = (...arg) => {
        arg.unshift(logPrefix[logTypes.warning], 'color: #ff9800;')
        logFunc(arg, console.warn);
    };

    logFunc.error = (...arg) => {
        if (arg[0] instanceof Error)
            method = getFunctionNameFromError(arg[0]);

        arg.unshift(logPrefix[logTypes.error], 'color: red;')
        logFunc(arg, console.error);
    };

    logFunc.logOutput = (func, args, classInstance) => {
        if (!logLevel.log) return func.apply(this, args);

        const observerLog = ClogFactory({
            ...config,
            method: false,
            newLine: false,
            trace: false,
        });

        const ts = performance.now();
        let classChanges, classProps = null;

        if (classInstance) {
            classProps = Object.getOwnPropertyNames(classInstance);
            classChanges = classProps.reduce((acc, prop) => {
                acc[prop] = classInstance[prop];
                return acc;
            }, {});
        }

        const funcName = uniqueId(func.name);

        // OBSERVING
        if (classInstance) {
            console.groupCollapsed('%cObserving:', 'color: #cc78ff', classInstance.constructor.name, funcName.replace('bound ', ''));
        } else {
            console.groupCollapsed('%cObserving:', 'color: #cc78ff', funcName);
        }

        const returning = func.apply(this, args);

        // TIME
        if (timer) observerLog.log('%c\t> Time:', 'color: #ffff88;', performance.now() - ts, 'ms');

        // ARGUMENTS
        if ([...args].length) observerLog.log('%c\t> Arguments:', 'color: #2aff2a;', ...args);

        if (classInstance) {
            const newProps = classProps.reduce((acc, prop) => {
                acc[prop] = classInstance[prop];
                return acc;
            }, {})

            classProps.forEach(e => {
                if (newProps[e] === classChanges[e]) {
                    delete classChanges[e]
                    delete newProps[e]
                }
            })

            if (Object.keys(classChanges).length) observerLog.log('%c\t> Old Props:', 'color: #7fffd4;', classChanges);
            if (Object.keys(newProps).length) observerLog.log('%c\t> New Props:', 'color: #7fffd4;', newProps);
        }

        // RETURNS
        if (returning) observerLog.log('%c\t> Returning:', 'color: #5e5eff;', returning);

        console.groupCollapsed('Function');
        observerLog.log(func);
        console.groupEnd();

        // TRACE
        if (trace && logLevel.trace) {
            console.groupCollapsed('Trace');
            console.trace();
            console.groupEnd();
        }
        console.groupEnd();

        if (newLine) console.log('');
        return returning;
    }

    logFunc.bindTo = (func, name) => {
        if (isInstanceOfClass(func)) {
            if (name)
                Object.defineProperty(func.constructor, 'name', { value: name, configurable: true });
            return bindToClassMethods(func);
        }

        if (typeof func !== 'function') {
            error('CLog not bound to an instance of a function', func);
            return;
        }

        if (!func.name)
            Object.defineProperty(func, 'name', { value: name || 'anonymous' });

        return (...args) => {
            try {
                return logOutput(func, args)
            } catch (ex) {
                error(ex, '\n\n', 'Arguments:', args);
                return ex;
            }
        }
    };


    logFunc.bindToClassMethods = (...args) => {
        if (!isInstanceOfClass(args[0])) {
            error('Not an instance of a Class', args[0]);
            return;
        }

        const classInstance = args[0];

        if (!classInstance.CLogBound) {
            error('CLog not bound to an instance of a bound class', classInstance);
            return;
        }

        if (args.length === 1) {
            const properties = Object.getOwnPropertyNames(Object.getPrototypeOf(classInstance));
            properties.forEach(prop => {
                if (typeof classInstance[prop] === 'function' && prop !== 'constructor') {
                    args.push(classInstance[prop])
                }
            });
        }

        args.forEach(e => {
            if (typeof e === 'object') return;
            if (typeof e === 'function') {
                const methodName = e.name.replace('bound ', '')
                classInstance[methodName] = function () {
                    return logOutput(e, arguments, classInstance)
                };
            } else {
                error('CLog not bound to an instance of a function', e.name.replace('bound ', ''));
            }
        })
    }

    logFunc.bindClass = (classInstance) => {
        if (!isInstanceOfClass(classInstance)) {
            error('Not an instance of a Class', classInstance);
            return;
        }

        classInstance.CLogerties = Object.getOwnPropertyNames(classInstance)
        classInstance.CLogBound = true;

        const properties = Object.getOwnPropertyNames(Object.getPrototypeOf(classInstance));

        properties.forEach(prop => {
            if (typeof classInstance[prop] === 'function' && prop !== 'constructor') {
                const originalFunction = classInstance[prop];
                const boundFunction = originalFunction.bind(classInstance);
                Object.defineProperty(boundFunction, 'name', { value: `bound ${prop}`, configurable: true });
                classInstance[prop] = boundFunction;
            }
        });

        return classInstance
    }

    logFunc.bindToWindow = (CLog) => {
        window.CLog = CLog;
    }

    const getFunctionNameFromError = (err) => {
        const re = /at (\w+)/g;
        const match = re.exec(err.stack.split('\n'));
        return match ? match[1] : 'unknown';
    }

    const isInstanceOfClass = (obj) => obj && typeof obj === 'object' && typeof obj.constructor === 'function';

    const { log, warn, error, logOutput, bindTo, bindToClassMethods, bindClass } = logFunc;

    return logFunc;
}